import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import {
  BookOpen, Download, Filter, ChevronRight, X,
  Search, Clock, LayoutGrid, Scale, CreditCard
} from "lucide-react";
import * as XLSX from "xlsx";

function money(n) {
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);
}
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d + "T12:00:00").toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" });
}

const TIPO_COLORS = {
  ingreso:   { bg: "#e0f2fe", color: "#0369a1", label: "Ingreso" },
  cobro:     { bg: "#dcfce7", color: "#166534", label: "Cobro" },
  gasto:     { bg: "#fee2e2", color: "#991b1b", label: "Gasto" },
  pago:      { bg: "#fef9c3", color: "#854d0e", label: "Pago" },
  ajuste:    { bg: "#f3e8ff", color: "#6b21a8", label: "Ajuste" },
  honorario: { bg: "#ffedd5", color: "#9a3412", label: "Honorario" },
};

function TipoBadge({ tipo }) {
  const s = TIPO_COLORS[tipo] || { bg: "#f1f5f9", color: "#475569", label: tipo };
  return <span style={{ background: s.bg, color: s.color, borderRadius: 6, padding: "3px 9px", fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>{s.label}</span>;
}

const thStyle = { padding: "10px 16px", textAlign: "left", color: "#94a3b8", fontWeight: 700, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" };
const trHover = { borderBottom: "1px solid #f8fafc", cursor: "pointer" };

/* ── Filter Slider ── */
function FiltroPanel({ open, onClose, filtros, onChange, periodos, onLimpiar }) {
  const hayFiltros = Object.values(filtros).some(v => v);
  const inp = { width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", color: "#0d1117", background: "#fff", boxSizing: "border-box" };
  return (
    <>
      {open && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "#00000030", zIndex: 150 }} />}
      <div style={{ position: "fixed", top: 0, right: 0, height: "100vh", width: 320, background: "#fff", boxShadow: "-8px 0 32px #00000015", zIndex: 160, display: "flex", flexDirection: "column", transform: open ? "translateX(0)" : "translateX(100%)", transition: "transform .3s cubic-bezier(.4,0,.2,1)", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0d1117" }}>Filtros</div>
            {hayFiltros && <div style={{ fontSize: 11, color: "#29abe2", marginTop: 2 }}>Filtros activos</div>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={20} /></button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Buscar</label>
            <div style={{ position: "relative" }}>
              <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
              <input value={filtros.busqueda} onChange={e => onChange("busqueda", e.target.value)} placeholder="Glosa o N° asiento..." style={{ ...inp, paddingLeft: 32 }} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Período</label>
            <select value={filtros.periodo} onChange={e => onChange("periodo", e.target.value)} style={{ ...inp, appearance: "none" }}>
              <option value="">Todos</option>
              {periodos.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Tipo</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[{ v: "", l: "Todos" }, { v: "ingreso", l: "Ingreso" }, { v: "cobro", l: "Cobro" }, { v: "gasto", l: "Gasto" }].map(({ v, l }) => (
                <button key={v} onClick={() => onChange("tipo", v)}
                  style={{ padding: "9px 14px", border: `1.5px solid ${filtros.tipo === v ? "#29abe2" : "#e2e8f0"}`, borderRadius: 8, background: filtros.tipo === v ? "#29abe210" : "#fff", color: filtros.tipo === v ? "#29abe2" : "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 8 }}>Rango fechas</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div><div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Desde</div><input type="date" value={filtros.fechaDesde} onChange={e => onChange("fechaDesde", e.target.value)} style={inp} /></div>
              <div><div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Hasta</div><input type="date" value={filtros.fechaHasta} onChange={e => onChange("fechaHasta", e.target.value)} style={inp} /></div>
            </div>
          </div>
        </div>
        {hayFiltros && (
          <div style={{ padding: "16px 24px", borderTop: "1px solid #e2e8f0" }}>
            <button onClick={onLimpiar} style={{ width: "100%", padding: "10px", border: "1.5px solid #fecaca", borderRadius: 8, background: "#fef2f2", color: "#dc2626", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Limpiar filtros</button>
          </div>
        )}
      </div>
    </>
  );
}

/* ── Modal Detalle Asiento ── */
function DetalleModal({ asiento, detalle, onClose }) {
  if (!asiento) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000040", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, fontFamily: "'DM Sans', sans-serif" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, width: 560, boxShadow: "0 20px 60px #00000020", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", background: "#f8fafc" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0d1117" }}>Asiento N° {asiento.numero}</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{asiento.glosa}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}><X size={18} /></button>
        </div>
        <div style={{ padding: "20px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
            {[["Fecha", fmtDate(asiento.fecha)], ["Período", asiento.periodo], ["Tipo", asiento.tipo]].map(([k, v]) => (
              <div key={k} style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{k}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0d1117", textTransform: "capitalize" }}>{v}</div>
              </div>
            ))}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#0d1117" }}>
                {["CÓDIGO", "CUENTA", "DEBE", "HABER"].map(h => (
                  <th key={h} style={{ padding: "9px 12px", textAlign: h === "DEBE" || h === "HABER" ? "right" : "left", color: "#fff", fontWeight: 700, fontSize: 10 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detalle.map((d, i) => (
                <tr key={d.id} style={{ borderBottom: "1px solid #f0f4f8", background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                  <td style={{ padding: "9px 12px", color: "#29abe2", fontFamily: "monospace", fontWeight: 700, fontSize: 11 }}>{d.cuenta_codigo}</td>
                  <td style={{ padding: "9px 12px", color: "#374151" }}>{d.cuenta_nombre}</td>
                  <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: d.debe > 0 ? 700 : 400, color: d.debe > 0 ? "#0d1117" : "#cbd5e1" }}>{d.debe > 0 ? money(d.debe) : "—"}</td>
                  <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: d.haber > 0 ? 700 : 400, color: d.haber > 0 ? "#0d1117" : "#cbd5e1" }}>{d.haber > 0 ? money(d.haber) : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
                <td colSpan={2} style={{ padding: "9px 12px", fontWeight: 800 }}>TOTALES</td>
                <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 800 }}>{money(asiento.total_debe)}</td>
                <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 800 }}>{money(asiento.total_haber)}</td>
              </tr>
            </tfoot>
          </table>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <span style={{ background: asiento.cuadrado ? "#e8f5e9" : "#fce4ec", color: asiento.cuadrado ? "#2e7d32" : "#c62828", borderRadius: 6, padding: "4px 12px", fontSize: 11, fontWeight: 700 }}>
              {asiento.cuadrado ? "✓ Cuadrado" : "✗ Descuadrado"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══ VISTA 1: Cronológica ══ */
function VistaCronologica({ asientos, detalles, onVerDetalle }) {
  const [pagina, setPagina] = useState(1);
  const POR_PAGINA = 25;
  const paginados = asientos.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
  const totalPaginas = Math.ceil(asientos.length / POR_PAGINA);
  const totalDebe = asientos.reduce((s, a) => s + (a.total_debe || 0), 0);
  const totalHaber = asientos.reduce((s, a) => s + (a.total_haber || 0), 0);

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 6px #0000000a" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              {["N°", "Fecha", "Período", "Tipo", "Glosa", "Debe", "Haber", "✓"].map(h => (
                <th key={h} style={{ ...thStyle, textAlign: h === "Debe" || h === "Haber" ? "right" : "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginados.map(a => (
              <tr key={a.id} style={trHover}
                onClick={() => onVerDetalle(a)}
                onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "11px 16px", fontWeight: 700, color: "#29abe2", fontFamily: "monospace" }}>{a.numero}</td>
                <td style={{ padding: "11px 16px", color: "#64748b", whiteSpace: "nowrap" }}>{fmtDate(a.fecha)}</td>
                <td style={{ padding: "11px 16px", color: "#64748b", fontFamily: "monospace", fontSize: 12 }}>{a.periodo}</td>
                <td style={{ padding: "11px 16px" }}><TipoBadge tipo={a.tipo} /></td>
                <td style={{ padding: "11px 16px", color: "#374151", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.glosa}</td>
                <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: 700 }}>{money(a.total_debe)}</td>
                <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: 700 }}>{money(a.total_haber)}</td>
                <td style={{ padding: "11px 16px" }}>
                  <span style={{ background: a.cuadrado ? "#e8f5e9" : "#fce4ec", color: a.cuadrado ? "#2e7d32" : "#c62828", borderRadius: 6, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>
                    {a.cuadrado ? "✓" : "✗"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
              <td colSpan={5} style={{ padding: "11px 16px", fontWeight: 800, fontSize: 13 }}>TOTALES</td>
              <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: 800, fontSize: 13 }}>{money(totalDebe)}</td>
              <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: 800, fontSize: 13 }}>{money(totalHaber)}</td>
              <td style={{ padding: "11px 16px" }}>
                <span style={{ background: totalDebe === totalHaber ? "#e8f5e9" : "#fce4ec", color: totalDebe === totalHaber ? "#2e7d32" : "#c62828", borderRadius: 6, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>
                  {totalDebe === totalHaber ? "OK" : "ERR"}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {totalPaginas > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderTop: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            {Math.min((pagina - 1) * POR_PAGINA + 1, asientos.length)}–{Math.min(pagina * POR_PAGINA, asientos.length)} de {asientos.length}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
              style={{ padding: "6px 14px", border: "1px solid #e2e8f0", borderRadius: 7, background: "#fff", cursor: pagina === 1 ? "not-allowed" : "pointer", color: pagina === 1 ? "#cbd5e1" : "#374151", fontSize: 12, fontFamily: "inherit" }}>‹</button>
            <span style={{ padding: "6px 14px", border: "1px solid #29abe2", borderRadius: 7, background: "#29abe2", color: "#fff", fontSize: 12, fontWeight: 700 }}>{pagina}/{totalPaginas}</span>
            <button onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))} disabled={pagina === totalPaginas}
              style={{ padding: "6px 14px", border: "1px solid #e2e8f0", borderRadius: 7, background: "#fff", cursor: pagina === totalPaginas ? "not-allowed" : "pointer", color: pagina === totalPaginas ? "#cbd5e1" : "#374151", fontSize: 12, fontFamily: "inherit" }}>›</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══ VISTA 2: Por Cuenta ══ */
function VistaPorCuenta({ detalleLineas }) {
  const [expandida, setExpandida] = useState(null);

  // Agrupar por cuenta
  const cuentas = {};
  detalleLineas.forEach(l => {
    const k = l.cuenta_codigo;
    if (!cuentas[k]) cuentas[k] = { codigo: k, nombre: l.cuenta_nombre, lineas: [], totalDebe: 0, totalHaber: 0 };
    cuentas[k].lineas.push(l);
    cuentas[k].totalDebe += l.debe || 0;
    cuentas[k].totalHaber += l.haber || 0;
  });
  const lista = Object.values(cuentas).sort((a, b) => a.codigo.localeCompare(b.codigo));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {lista.map(c => {
        const saldo = c.totalDebe - c.totalHaber;
        const open = expandida === c.codigo;
        return (
          <div key={c.codigo} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px #0000000a" }}>
            <div onClick={() => setExpandida(open ? null : c.codigo)}
              style={{ padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", background: open ? "#f0f9ff" : "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ fontFamily: "monospace", fontWeight: 800, color: "#29abe2", fontSize: 14 }}>{c.codigo}</span>
                <span style={{ fontWeight: 600, color: "#0d1117", fontSize: 14 }}>{c.nombre}</span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>{c.lineas.length} movimientos</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 20, fontSize: 13 }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 10, color: "#94a3b8", letterSpacing: 1 }}>DEBE</div>
                  <div style={{ fontWeight: 700 }}>{money(c.totalDebe)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 10, color: "#94a3b8", letterSpacing: 1 }}>HABER</div>
                  <div style={{ fontWeight: 700 }}>{money(c.totalHaber)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 10, color: "#94a3b8", letterSpacing: 1 }}>SALDO</div>
                  <div style={{ fontWeight: 800, color: saldo >= 0 ? "#10b981" : "#ef4444" }}>{money(Math.abs(saldo))}</div>
                </div>
                <ChevronRight size={16} color="#94a3b8" style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }} />
              </div>
            </div>
            {open && (
              <div style={{ borderTop: "1px solid #e2e8f0", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      {["Fecha", "Glosa", "Debe", "Haber", "Saldo"].map(h => (
                        <th key={h} style={{ ...thStyle, fontSize: 9, textAlign: h === "Debe" || h === "Haber" || h === "Saldo" ? "right" : "left" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {c.lineas.map((l, i) => {
                      const saldoParcial = c.lineas.slice(0, i + 1).reduce((s, x) => s + (x.debe || 0) - (x.haber || 0), 0);
                      return (
                        <tr key={l.id} style={{ borderBottom: "1px solid #f8fafc" }}>
                          <td style={{ padding: "8px 16px", color: "#64748b", whiteSpace: "nowrap" }}>{fmtDate(l.asiento_fecha)}</td>
                          <td style={{ padding: "8px 16px", color: "#374151", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.glosa}</td>
                          <td style={{ padding: "8px 16px", textAlign: "right", color: l.debe > 0 ? "#0d1117" : "#cbd5e1", fontWeight: l.debe > 0 ? 700 : 400 }}>{l.debe > 0 ? money(l.debe) : "—"}</td>
                          <td style={{ padding: "8px 16px", textAlign: "right", color: l.haber > 0 ? "#0d1117" : "#cbd5e1", fontWeight: l.haber > 0 ? 700 : 400 }}>{l.haber > 0 ? money(l.haber) : "—"}</td>
                          <td style={{ padding: "8px 16px", textAlign: "right", fontWeight: 700, color: saldoParcial >= 0 ? "#10b981" : "#ef4444" }}>{money(Math.abs(saldoParcial))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ══ VISTA 3: Balance de Comprobación ══ */
function VistaBalance({ detalleLineas }) {
  const cuentas = {};
  detalleLineas.forEach(l => {
    const k = l.cuenta_codigo;
    if (!cuentas[k]) cuentas[k] = { codigo: k, nombre: l.cuenta_nombre, debe: 0, haber: 0 };
    cuentas[k].debe += l.debe || 0;
    cuentas[k].haber += l.haber || 0;
  });
  const lista = Object.values(cuentas).sort((a, b) => a.codigo.localeCompare(b.codigo));
  const totDebe = lista.reduce((s, c) => s + c.debe, 0);
  const totHaber = lista.reduce((s, c) => s + c.haber, 0);

  function exportar() {
    const rows = lista.map(c => ({ "Código": c.codigo, "Cuenta": c.nombre, "Debe": c.debe, "Haber": c.haber, "Saldo": c.debe - c.haber }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Balance Comprobación");
    XLSX.writeFile(wb, `balance_comprobacion_${new Date().toISOString().split("T")[0]}.xlsx`);
  }

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 6px #0000000a" }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0d1117" }}>⚖️ Balance de Comprobación · {lista.length} cuentas</span>
        <button onClick={exportar} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", border: "1.5px solid #e2e8f0", borderRadius: 7, background: "#fff", color: "#374151", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          <Download size={12} /> Excel
        </button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              {["Código", "Cuenta", "Total Debe", "Total Haber", "Saldo"].map(h => (
                <th key={h} style={{ ...thStyle, textAlign: h !== "Código" && h !== "Cuenta" ? "right" : "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lista.map((c, i) => {
              const saldo = c.debe - c.haber;
              return (
                <tr key={c.codigo} style={{ borderBottom: "1px solid #f8fafc", background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                  <td style={{ padding: "10px 16px", fontFamily: "monospace", fontWeight: 700, color: "#29abe2", fontSize: 12 }}>{c.codigo}</td>
                  <td style={{ padding: "10px 16px", color: "#374151", fontWeight: 500 }}>{c.nombre}</td>
                  <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 600 }}>{money(c.debe)}</td>
                  <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 600 }}>{money(c.haber)}</td>
                  <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 800, color: saldo >= 0 ? "#10b981" : "#ef4444" }}>{money(Math.abs(saldo))}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f8fafc", borderTop: "2px solid #0d1117" }}>
              <td colSpan={2} style={{ padding: "12px 16px", fontWeight: 800, fontSize: 13 }}>TOTALES</td>
              <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 800, fontSize: 13 }}>{money(totDebe)}</td>
              <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 800, fontSize: 13 }}>{money(totHaber)}</td>
              <td style={{ padding: "12px 16px", textAlign: "right" }}>
                <span style={{ background: totDebe === totHaber ? "#e8f5e9" : "#fce4ec", color: totDebe === totHaber ? "#2e7d32" : "#c62828", borderRadius: 6, padding: "4px 12px", fontSize: 11, fontWeight: 800 }}>
                  {totDebe === totHaber ? "✓ Cuadrado" : "✗ Descuadrado"}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ══ VISTA 4: Kardex por Cuenta ══ */
function VistaKardex({ detalleLineas }) {
  const cuentas = [...new Set(detalleLineas.map(l => l.cuenta_codigo))].sort();
  const [cuentaSel, setCuentaSel] = useState(cuentas[0] || "");

  const lineas = detalleLineas.filter(l => l.cuenta_codigo === cuentaSel).sort((a, b) => {
    if (a.asiento_fecha < b.asiento_fecha) return -1;
    if (a.asiento_fecha > b.asiento_fecha) return 1;
    return 0;
  });

  let saldoAcum = 0;
  const conSaldo = lineas.map(l => {
    saldoAcum += (l.debe || 0) - (l.haber || 0);
    return { ...l, saldoAcum };
  });

  const nombreCuenta = lineas[0]?.cuenta_nombre || cuentaSel;

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 6px #0000000a" }}>
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0d1117", whiteSpace: "nowrap" }}>📋 Kardex ·</div>
        <select value={cuentaSel} onChange={e => setCuentaSel(e.target.value)}
          style={{ flex: 1, padding: "8px 12px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", color: "#0d1117", appearance: "none" }}>
          {cuentas.map(c => {
            const nombre = detalleLineas.find(l => l.cuenta_codigo === c)?.cuenta_nombre || c;
            return <option key={c} value={c}>{c} · {nombre}</option>;
          })}
        </select>
      </div>

      {conSaldo.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Sin movimientos para esta cuenta</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                {["Fecha", "N° Asiento", "Glosa", "Debe", "Haber", "Saldo"].map(h => (
                  <th key={h} style={{ ...thStyle, textAlign: h === "Debe" || h === "Haber" || h === "Saldo" ? "right" : "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {conSaldo.map((l, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f8fafc" }}>
                  <td style={{ padding: "11px 16px", color: "#64748b", whiteSpace: "nowrap" }}>{fmtDate(l.asiento_fecha)}</td>
                  <td style={{ padding: "11px 16px", color: "#29abe2", fontFamily: "monospace", fontWeight: 700 }}>{l.asiento_numero}</td>
                  <td style={{ padding: "11px 16px", color: "#374151", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.glosa}</td>
                  <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: l.debe > 0 ? 700 : 400, color: l.debe > 0 ? "#10b981" : "#cbd5e1" }}>{l.debe > 0 ? money(l.debe) : "—"}</td>
                  <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: l.haber > 0 ? 700 : 400, color: l.haber > 0 ? "#ef4444" : "#cbd5e1" }}>{l.haber > 0 ? money(l.haber) : "—"}</td>
                  <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: 800, color: l.saldoAcum >= 0 ? "#10b981" : "#ef4444" }}>
                    {money(Math.abs(l.saldoAcum))} <span style={{ fontSize: 10, color: "#94a3b8" }}>{l.saldoAcum >= 0 ? "D" : "H"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
                <td colSpan={3} style={{ padding: "11px 16px", fontWeight: 800, fontSize: 13 }}>SALDO FINAL · {nombreCuenta}</td>
                <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: 800 }}>{money(conSaldo.reduce((s, l) => s + (l.debe || 0), 0))}</td>
                <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: 800 }}>{money(conSaldo.reduce((s, l) => s + (l.haber || 0), 0))}</td>
                <td style={{ padding: "11px 16px", textAlign: "right", fontWeight: 800, color: conSaldo[conSaldo.length - 1]?.saldoAcum >= 0 ? "#10b981" : "#ef4444" }}>
                  {money(Math.abs(conSaldo[conSaldo.length - 1]?.saldoAcum || 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══ APP PRINCIPAL ══ */
const VISTAS = [
  { id: "cronologica", label: "Cronológico",          icon: Clock },
  { id: "cuentas",     label: "Por Cuenta",            icon: LayoutGrid },
  { id: "balance",     label: "Balance Comprobación",  icon: Scale },
  { id: "kardex",      label: "Kardex",                icon: CreditCard },
];

export default function LibroMayor() {
  const [vista, setVista] = useState("cronologica");
  const [asientos, setAsientos] = useState([]);
  const [detalleLineas, setDetalleLineas] = useState([]);
  const [detalles, setDetalles] = useState({});
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroOpen, setFiltroOpen] = useState(false);
  const [periodos, setPeriodos] = useState([]);
  const [filtros, setFiltros] = useState({ busqueda: "", periodo: "", tipo: "", fechaDesde: "", fechaHasta: "" });

  function setFiltro(k, v) { setFiltros(f => ({ ...f, [k]: v })); }
  function limpiarFiltros() { setFiltros({ busqueda: "", periodo: "", tipo: "", fechaDesde: "", fechaHasta: "" }); }
  const hayFiltros = Object.values(filtros).some(v => v);

  useEffect(() => { cargar(); }, [filtros.periodo, filtros.tipo, filtros.fechaDesde, filtros.fechaHasta]);

  async function cargar() {
    setLoading(true);
    let q = supabase.from("asientos").select("*").eq("estado", "contabilizado").order("fecha").order("numero");
    if (filtros.periodo) q = q.eq("periodo", filtros.periodo);
    if (filtros.tipo) q = q.eq("tipo", filtros.tipo);
    if (filtros.fechaDesde) q = q.gte("fecha", filtros.fechaDesde);
    if (filtros.fechaHasta) q = q.lte("fecha", filtros.fechaHasta);
    const { data: aa } = await q;
    setAsientos(aa || []);

    // Para vistas de cuenta/balance/kardex necesitamos el detalle completo con fecha
    if (aa?.length) {
      const ids = aa.map(a => a.id);
      const { data: dd } = await supabase.from("asiento_detalle").select("*").in("asiento_id", ids);
      // Enriquecer con fecha y número del asiento padre
      const asientoMap = {};
      aa.forEach(a => { asientoMap[a.id] = a; });
      const enriched = (dd || []).map(d => ({
        ...d,
        asiento_fecha: asientoMap[d.asiento_id]?.fecha,
        asiento_numero: asientoMap[d.asiento_id]?.numero,
        glosa: d.glosa || asientoMap[d.asiento_id]?.glosa,
      }));
      setDetalleLineas(enriched);
    } else {
      setDetalleLineas([]);
    }

    const { data: ps } = await supabase.from("asientos").select("periodo").order("periodo", { ascending: false });
    setPeriodos([...new Set((ps || []).map(p => p.periodo).filter(Boolean))]);
    setLoading(false);
  }

  async function verDetalle(asiento) {
    if (!detalles[asiento.id]) {
      const { data } = await supabase.from("asiento_detalle").select("*").eq("asiento_id", asiento.id).order("orden");
      setDetalles(prev => ({ ...prev, [asiento.id]: data || [] }));
    }
    setSelected(asiento);
  }

  const filtrados = asientos.filter(a => {
    if (!filtros.busqueda) return true;
    const q = filtros.busqueda.toLowerCase();
    return (a.glosa || "").toLowerCase().includes(q) || String(a.numero || "").includes(q);
  });

  const totalDebe = filtrados.reduce((s, a) => s + (a.total_debe || 0), 0);
  const totalHaber = filtrados.reduce((s, a) => s + (a.total_haber || 0), 0);

  function exportarExcel() {
    const rows = filtrados.map(a => ({ "N°": a.numero, "Fecha": a.fecha, "Período": a.periodo, "Tipo": a.tipo, "Glosa": a.glosa, "Debe": a.total_debe, "Haber": a.total_haber, "Cuadrado": a.cuadrado ? "Sí" : "No" }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Libro Mayor");
    XLSX.writeFile(wb, `libro_mayor_${new Date().toISOString().split("T")[0]}.xlsx`);
  }

  return (
    <div style={{ padding: "28px 32px", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0d1117", margin: 0 }}>Libro Mayor</h1>
          <p style={{ color: "#94a3b8", fontSize: 13, margin: "4px 0 0" }}>Contabilidad de partida doble · {filtrados.length} asientos</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={exportarExcel}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            <Download size={14} /> Excel
          </button>
          <button onClick={() => setFiltroOpen(true)}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", border: `1.5px solid ${hayFiltros ? "#29abe2" : "#e2e8f0"}`, borderRadius: 8, background: hayFiltros ? "#29abe210" : "#fff", color: hayFiltros ? "#29abe2" : "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            <Filter size={14} /> Filtros {hayFiltros && <span style={{ background: "#29abe2", color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 10 }}>ON</span>}
            <ChevronRight size={13} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: 14, marginBottom: 24 }}>
        {[
          { label: "Asientos", value: filtrados.length, color: "#29abe2" },
          { label: "Total Debe", value: money(totalDebe), color: "#10b981" },
          { label: "Total Haber", value: money(totalHaber), color: "#f59e0b" },
          { label: "Estado", value: totalDebe === totalHaber ? "✓ Cuadrado" : "✗ Error", color: totalDebe === totalHaber ? "#10b981" : "#ef4444" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ flex: 1, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 22px", boxShadow: "0 1px 6px #0000000a" }}>
            <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Tabs vistas */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, background: "#f8fafc", borderRadius: 10, padding: 4, border: "1px solid #e2e8f0", width: "fit-content" }}>
        {VISTAS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setVista(id)}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 7, border: "none", background: vista === id ? "#fff" : "transparent", color: vista === id ? "#0d1117" : "#94a3b8", fontSize: 12, fontWeight: vista === id ? 700 : 500, cursor: "pointer", fontFamily: "inherit", boxShadow: vista === id ? "0 1px 4px #00000010" : "none", transition: "all .15s" }}>
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      {loading ? (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 48, textAlign: "center", color: "#94a3b8" }}>Cargando...</div>
      ) : filtrados.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 48, textAlign: "center", color: "#94a3b8" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📒</div>
          <div style={{ fontWeight: 700, color: "#64748b" }}>Sin asientos contables</div>
        </div>
      ) : (
        <>
          {vista === "cronologica" && <VistaCronologica asientos={filtrados} detalles={detalles} onVerDetalle={verDetalle} />}
          {vista === "cuentas" && <VistaPorCuenta detalleLineas={detalleLineas} />}
          {vista === "balance" && <VistaBalance detalleLineas={detalleLineas} />}
          {vista === "kardex" && <VistaKardex detalleLineas={detalleLineas} />}
        </>
      )}

      <FiltroPanel open={filtroOpen} onClose={() => setFiltroOpen(false)} filtros={filtros} onChange={setFiltro} periodos={periodos} onLimpiar={limpiarFiltros} />
      {selected && <DetalleModal asiento={selected} detalle={detalles[selected.id] || []} onClose={() => setSelected(null)} />}

      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>
    </div>
  );
}